import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isEmpty } from 'lodash';
import {
  AuthControllerApi,
  IdentitySummaryResponse,
  JwtAuthenticationResponse,
  KeyMakerControllerApi,
  LoginRequest,
  MfaControllerApi,
  UserPrincipal,
} from '../openapi/keymaker';
import {
  UserControllerApi,
  UserPublicInfo,
  UserResponse,
  UserResponseAgentStatusEnum,
  UserResponseTypeEnum,
} from '../openapi/yenta';
import ErrorService from '../services/ErrorService.ts';
import { AppThunk, AuthState, ErrorCode, meResponseData } from '../types';
import { resetApp } from '../utils/authActions';
import {
  deleteAuthCookie,
  setAuthCookie,
  setUserInLogServices,
} from '../utils/AuthUtils';
import Logger from '../utils/Logger';
import {
  getKeymakerConfiguration,
  getYentaConfiguration,
} from '../utils/OpenapiConfigurationUtils';
import { redirectToBolt } from '../utils/UrlUtils.ts';
import { fetchLoans } from './LoanSlice.ts';
import { showErrorToast, showSuccessToast } from './ToastNotificationSlice';

/**
 * Checks if the current path is a signup or onboarding route where
 * session expiration toasts should be suppressed
 */
const isSignupOrOnboardingRoute = (): boolean => {
  const pathname = window.location.pathname;
  return pathname.startsWith('/rezen-signup');
};

export const initialState: AuthState = {
  loadingAuthUserDetail: false,
  authUserDetail: undefined,
  keymakerCurrentUser: undefined,
  jwtAuthResponseCurrentUser: undefined,
  userById: {},
  appCompatible: false,
};

const AuthSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    saveAuthUserDetail(state, action: PayloadAction<UserResponse | undefined>) {
      state.authUserDetail = action.payload;
    },
    setLoadingAuthUserDetail(state, action: PayloadAction<boolean>) {
      state.loadingAuthUserDetail = action.payload;
    },
    saveKeymakerCurrentUser: (
      state,
      action: PayloadAction<IdentitySummaryResponse>,
    ) => {
      state.keymakerCurrentUser = action.payload;
    },
    saveJwtAuthResponseCurrentUser: (
      state,
      action: PayloadAction<JwtAuthenticationResponse>,
    ) => {
      state.jwtAuthResponseCurrentUser = action.payload;
    },
    saveAppCompatibility(state, action: PayloadAction<boolean>) {
      state.appCompatible = action.payload;
    },
    saveUserById(state, action: PayloadAction<Array<UserPublicInfo>>) {
      action.payload.forEach((userInfo) => {
        state.userById[userInfo.id!] = userInfo;
      });
    },
  },
});

export const {
  saveAuthUserDetail,
  setLoadingAuthUserDetail,
  saveKeymakerCurrentUser,
  saveJwtAuthResponseCurrentUser,
  saveUserById,
  saveAppCompatibility,
} = AuthSlice.actions;

export const loginUser =
  (req: LoginRequest): AppThunk<Promise<JwtAuthenticationResponse | null>> =>
  async (dispatch) => {
    try {
      const { data } = await new AuthControllerApi(
        await getKeymakerConfiguration(),
      ).authenticateUser(req);
      dispatch(saveJwtAuthResponseCurrentUser(data));
      setAuthCookie(data.accessToken!);
      return data;
    } catch (e) {
      Logger.error('Unable to login user. Throwing error.', e);
      throw e;
    }
  };

export const fetchAuthUserDetail =
  (
    leadId?: string,
    isOnboardingUser: boolean = false,
    loading = true,
  ): AppThunk<Promise<meResponseData>> =>
  async (dispatch) => {
    let shouldRedirectToBolt = false;

    if (loading) {
      dispatch(setLoadingAuthUserDetail(true));
    }

    try {
      const keymakerCurrentUser = await dispatch(
        fetchKeymakerCurrentUser(true),
      );

      if (!keymakerCurrentUser) {
        // Do nothing, if the API fails it redirects to /login
        // and if API works, everything will be normal
        return { isLoginSuccess: false };
      }

      const { data } = await new UserControllerApi(
        await getYentaConfiguration(),
      ).getCurrentGenericUser();

      if (
        data.agentStatus === UserResponseAgentStatusEnum.Rejected &&
        data.type === UserResponseTypeEnum.Agent
      ) {
        await dispatch(signOutUser());
        dispatch(
          showErrorToast(
            'Your account is in a rejected status. Please contact support for more information.',
            undefined,
            4000,
          ),
        );
        return { isLoginSuccess: false };
      }

      if (
        data.type === UserResponseTypeEnum.Agent &&
        data.agentStatus === UserResponseAgentStatusEnum.Active
      ) {
        dispatch(setLoadingAuthUserDetail(true));
        shouldRedirectToBolt = true;
        redirectToBolt();
        deleteAuthCookie();
        return { isLoginSuccess: false };
      }

      if (
        data.type === UserResponseTypeEnum.Generic ||
        !isEmpty(data.onboardings)
      ) {
        // Call loans API only if user is not Onboarding user and onboarding data is empty
        if (!isOnboardingUser && isEmpty(data.onboardings)) {
          const isBorrowerSuccess = await dispatch(fetchLoans(leadId));
          if (!isBorrowerSuccess) {
            Logger.log('Oops! Something went wrong. Please login again.');
            return { isLoginSuccess: false };
          }
        }

        // If user is Onboarding user, then don't save user details in redux.
        // Prevents user redirection to home page in signup
        if (!isOnboardingUser) {
          dispatch(saveAuthUserDetail(data));
        }

        setUserInLogServices(data);

        return { isLoginSuccess: true, userData: data };
      }

      deleteAuthCookie();
      return { isLoginSuccess: false };
    } catch (e: any) {
      Logger.error('Token expired. Please login again', e);
      ErrorService.notify('Token expired. Please login again', e);
      dispatch(showErrorToast('Token expired. Please login again'));
      deleteAuthCookie();
      return { isLoginSuccess: false };
    } finally {
      if (loading && !shouldRedirectToBolt) {
        dispatch(setLoadingAuthUserDetail(false));
      }
    }
  };

export const refetchAuthUserDetail =
  (hideToastMessage: boolean = false): AppThunk<Promise<meResponseData>> =>
  async (dispatch) => {
    try {
      const { data } = await new UserControllerApi(
        await getYentaConfiguration(),
      ).getCurrentGenericUser();

      return { isLoginSuccess: true, userData: data };
    } catch (e: any) {
      Logger.error('Token expired. Please login again', e);
      ErrorService.notify('Token expired. Please login again', e);
      if (
        !(
          ErrorService.getErrorCode(e) === ErrorCode.FORBIDDEN &&
          hideToastMessage
        )
      ) {
        dispatch(showErrorToast('Token expired. Please login again'));
      }
      deleteAuthCookie();
      return { isLoginSuccess: false };
    }
  };

export const fetchKeymakerCurrentUser =
  (
    showToastMessage: boolean = false,
  ): AppThunk<Promise<IdentitySummaryResponse | undefined>> =>
  async (dispatch) => {
    try {
      const { data } = await new KeyMakerControllerApi(
        await getKeymakerConfiguration(),
      ).getCurrentUser();
      dispatch(saveKeymakerCurrentUser(data));
      return data!;
    } catch (e: any) {
      if (e.response?.status === 403) {
        deleteAuthCookie();
        dispatch(resetApp());
        // Skip showing toast if on signup/onboarding routes
        // to prevent error messages when users land from email invitation links
        const shouldSkipToast = isSignupOrOnboardingRoute();
        if (!shouldSkipToast) {
          dispatch(
            showErrorToast('Your session has expired. Please log back in.'),
          );
        }
      } else if (showToastMessage) {
        dispatch(
          showErrorToast(
            'Oops! Something went wrong. Please refresh the page and try again.',
          ),
        );
      }
      ErrorService.notifyIgnoreAuthErrors(
        'Unable to fetch keymaker current user',
        e,
      );

      return undefined;
    }
  };

export const mfaSignIn =
  (code: number): AppThunk =>
  async (dispatch) => {
    try {
      const { data } = await new MfaControllerApi(
        await getKeymakerConfiguration(),
      ).signInWithMfa({
        code: code,
      });
      setAuthCookie(data.accessToken!);
      await dispatch(fetchAuthUserDetail());
    } catch (e: any) {
      ErrorService.notify(
        'Unable to signin with two factor authentication',
        e,
        {
          data: {
            code: code,
          },
        },
      );
      dispatch(
        showErrorToast(
          'We had a problem signing in with two factor authentication',
          'Please try again in a few moments.',
        ),
      );
    }
  };

export const signOutUser = (): AppThunk<Promise<void>> => async (dispatch) => {
  try {
    await new AuthControllerApi(await getKeymakerConfiguration()).signOut();
    deleteAuthCookie();
    dispatch(resetApp());
  } catch (e: any) {
    Logger.error('Unable to logout', e);
    ErrorService.notify('Unable to logout', e);
  }
};

export const sendEmailVerificationToken =
  (): AppThunk<Promise<boolean>> => async (dispatch) => {
    try {
      await new AuthControllerApi(
        await getKeymakerConfiguration(),
      ).sendEmailVerificationToken();
      dispatch(
        showSuccessToast(
          'An email verification token has been sent to your email.',
          'Please check your email for the verification link.',
        ),
      );
      return true;
    } catch (e: any) {
      dispatch(
        showErrorToast(
          'We had a problem sending an email for verification.',
          'Please try again in a few moments.',
        ),
      );
      ErrorService.notify('Unable to send email verification token', e);
      return false;
    }
  };
export const verifyEmail =
  (token: string): AppThunk<Promise<boolean>> =>
  async (dispatch) => {
    try {
      await new AuthControllerApi(await getKeymakerConfiguration()).verifyEmail(
        token,
      );
      dispatch(
        showSuccessToast(
          'Your email has been verified.',
          'You can now log in to your account.',
        ),
      );
      return true;
    } catch (e: any) {
      ErrorService.notify('Unable to verify email', e);
      dispatch(
        showErrorToast(
          e?.response?.data?.['com.real.commons.apierror.ApiError']?.message ??
            'We had a problem verifying your email.',
          'Please try again in a few moments.',
        ),
      );
      return false;
    }
  };

export const verifyJWT =
  (jwtToken: string): AppThunk<Promise<UserPrincipal | null>> =>
  async () => {
    try {
      const { data } = await new AuthControllerApi(
        await getKeymakerConfiguration(),
      ).verifyToken(jwtToken);

      return data;
    } catch (e: any) {
      ErrorService.notifyIgnoreAuthErrors('Unable to verify JWT token', e);
      return null;
    }
  };

export default AuthSlice.reducer;
